"use client";
import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Icon from "../icons/Icons";
import { ROOT_URL } from "@/utils/gloableVariables";
import { SchemesByUCode } from "@/type/commonTypes";
import HomeSectionsBadge from "../others/HomeSectionsBadge";
import { formatIndianNumber } from "@/utils/commonFunctions";
import HomeSectionHeading from "../others/HomeSectionHeading";

type Props = {
  fundsData: Record<string, SchemesByUCode>;
};

const ANIMATED_TEXT = "Live Rankings";
export default function HomeFundAnalysisSection({ fundsData }: Props) {

  const ref = useRef<HTMLDivElement | null>(null);
  const [startAnimation, setStartAnimation] = useState(false);
  const [showPulse, setShowPulse] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStartAnimation(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!startAnimation) return;

    const timer = setTimeout(() => {
      setShowPulse(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, [startAnimation]);

  return (
    <div className="section grid items-center gap-10 sm:grid-cols-[1fr_380px] lg:gap-16">
      {/* Left Content */}
      <div className="w-full max-w-139">
        <HomeSectionsBadge label="Best Mutual Funds" className="text-white bg-white/10 border border-white/10 mb-4" />

        <HomeSectionHeading
          heading={`We Do the Research. You Get the <i class="text-[#00FE15] font-black">Shortlist</i>`}
          subHeading="Real-time analysis across 2000+ funds, narrowed down to a curated list you can actually act on."
          textAlign="text-left"
          spacing="mb-15"
          headingColor="text-white"
          subHeadingColor="hidden sm:block text-white/50"
        />

        <div className="hidden sm:flex items-center gap-12">
          <div className="space-y-2">
            <div className="text-4xl font-extrabold text-white">100+</div>
            <div className="uppercase tracking-[-0.45px] italic text-white/50">Data Points</div>
          </div>

          <div className="space-y-2">
            <div className="text-4xl font-extrabold text-white">Top 15</div>
            <div className="uppercase tracking-[-0.45px] italic text-white/50">Curated Funds</div>
          </div>
        </div>
      </div>

      {/* Right Card */}
      <div className="relative mx-auto w-full sm:max-w-109.5 overflow-hidden rounded-4xl sm:rounded-10xl border border-white/20 bg-white/5 shadow-[0px_25px_50px_-12px_#00000040] p-4 sm:p-8">
        <div className="flex items-start justify-between mb-8">
          <div className="space-y-1">
            {/* <div className="flex items-center gap-2.5">
              <span className="shrink-0 h-2 w-2 rounded-full bg-secondaryGreen" />
              <p className="sm:text-xl tracking-[-0.45px] font-extrabold text-white">Live Rankings</p>
            </div> */}

            <div ref={ref} className="flex items-center gap-2.5">
              <span
                className={["h-2 w-2 shrink-0 rounded-full bg-secondaryGreen", showPulse ? "animate-pulse" : ""].join(" ")}
              />

              <p className="sm:text-xl font-extrabold tracking-[-0.45px] text-white">
                {ANIMATED_TEXT.split("").map((char, index) => (
                  <span
                    key={`${char}-${index}`}
                    className={["inline-block opacity-0", startAnimation ? "animate-letter-reveal" : ""].join(" ")}
                    style={{
                      animationDelay: `${index * 0.08}s`,
                      animationFillMode: "forwards",
                    }}
                  >
                    {char === " " ? "\u00A0" : char}
                  </span>
                ))}
              </p>
            </div>

            <p className="text-2xs sm:text-sm tracking-[-0.15px] text-[#99A1AF]">Top Mutual Fund Picks</p>
          </div>

          <span className="w-10 h-10 rounded-full bg-white/10 inline-flex items-center justify-center text-white">
            <Icon.growthSVG className="w-5 h-5" />
          </span>
        </div>

        <div className="space-y-3 mb-6">
          {Object.values(fundsData).map((fund, ind) => (
            <Link
              href={`/mutual-fund/${fund.url}/mso${fund.id}`}
              key={fund.id}
              className="group flex items-center gap-4 rounded-2xl border border-white/5 bg-white/5 p-3 sm:p-4"
            >
              <span className="h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-1.5xl bg-white/10 border border-white/5 text-sm font-bold text-white">
                {ind + 1}
              </span>

              <div className="space-y-1">
                <p className="text-sm sm:text-2sm font-bold tracking-[-0.23px] text-white line-clamp-1 transition group-hover:text-[#8bbfff]">
                  {fund.scheme_name}
                </p>

                <p className="text-xs font-medium uppercase text-[#99A1AF]">
                  {fund.category_type}
                  <span className="mx-1">•</span>
                  NAV ₹{formatIndianNumber(fund.latest_nav)}
                </p>
              </div>

              <div className="text-right ml-auto">
                <div
                  className={[
                    "text-2sm font-extrabold",
                    fund.three_year > 0 ? "text-[#00FF66]" : "text-[#ff0000]",
                  ].join(" ")}
                >
                  {Number(fund.three_year).toFixed(1)}%
                </div>
                <div className="text-[10px] leading-1.5 font-bold uppercase text-[#6A7282]">3Y Rt.</div>
              </div>
            </Link>
          ))}
        </div>

        <a
          href={`${ROOT_URL}best-funds`}
          className="flex relative w-full items-center justify-center gap-2 py-2.5 sm:py-3.5 border border-white rounded-xl sm:rounded-2xl bg-white text-sm sm:text-base tracking-[-0.23px] font-bold text-black transition sm:hover:text-white sm:hover:bg-transparent"
        >
          Explore All Ranked Funds
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M5 12H19" stroke="currentColor" strokeWidth="2" />
            <path d="M13 6L19 12L13 18" stroke="currentColor" strokeWidth="2" />
          </svg>
        </a>

        {/* Glow */}
        <div className="absolute z-1 pointer-events-none -right-6 -top-6 h-24 w-24 bg-[#2B7FFF]/50 blur-3xl" />
        <div className="absolute z-1 pointer-events-none -bottom-10 -left-6 h-32 w-32 bg-[#AD46FF]/35 blur-3xl" />
      </div>

      {/* Highlighted Tag */}
      <span className="inline-block w-600 h-600 absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 bg-[#155DFC33] blur-[200px]" />
    </div>
  );
}
